import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, lazy, Suspense } from "react";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import ErrorBoundary from "./components/ErrorBoundary";
import { ChunkErrorBoundary } from "./components/ChunkErrorBoundary";
import ReconnectingBanner from "./components/ReconnectingBanner";
import Layout from "./components/Layout";
import PinLogin from "./pages/PinLogin";
import { useVersionCheck } from "./hooks/useVersionCheck";
import { UpdateAvailableBanner } from "./components/UpdateAvailableBanner";
import "./lib/networkMiddleware"; // Initialize resilient network layer
import { canAccessFloorCheck } from "./lib/floorCheckAccess";
import { canAccessTeamReviews } from "./lib/teamReviewsAccess";
import { useWebSocket } from "./hooks/useWebSocket";
import { installWsCacheBridge } from "./lib/wsCacheBridge";
import { initTheme } from "./lib/theme";

// iter 373 — ONE app-wide WebSocket connection for every authenticated
// route. Previously only Layout mounted useWebSocket(), so standalone
// routes (/shop, /detailer, /inventory-dash, /mobile/*, /shop/build/*)
// had ws_event listeners that never fired — inventory changes made on
// the shop floor never reached those open pages until a manual reload.
function GlobalWebSocket() {
  useWebSocket();
  useEffect(() => { installWsCacheBridge(); }, []);
  return null;
}

// Lazy-loaded pages with retry for chunk load failures
const RouteBuilder = lazyRetry(() => import("./pages/RouteBuilder"));
const ServiceCalls = lazyRetry(() => import("./pages/ServiceCalls"));
const ScheduleCalendar = lazyRetry(() => import("./pages/ScheduleCalendar"));
const ServiceTicketHistory = lazyRetry(() => import("./pages/ServiceTicketHistory"));
const Deliveries = lazyRetry(() => import("./pages/Deliveries"));
const Tasks = lazyRetry(() => import("./pages/Tasks"));
const DailyFloorCheck = lazyRetry(() => import("./pages/DailyFloorCheck"));
const Bulletins = lazyRetry(() => import("./pages/Bulletins"));
const KnowledgeBase = lazyRetry(() => import("./pages/KnowledgeBase"));
const Diagnostics = lazyRetry(() => import("./pages/Diagnostics"));
const DriverPortal = lazyRetry(() => import("./pages/DriverPortal"));
const Settings = lazyRetry(() => import("./pages/Settings"));
const BuildSheetLibrary = lazyRetry(() => import("./pages/BuildSheetLibrary"));
const RecallChecklistAdmin = lazyRetry(() => import("./pages/RecallChecklistAdmin"));
const CartAudit = lazyRetry(() => import("./pages/CartAudit"));
const CartAuditLog = lazyRetry(() => import("./pages/CartAuditLog"));
const CustomerHistory = lazyRetry(() => import("./pages/CustomerHistory"));
const FleetMaintenance = lazyRetry(() => import("./pages/FleetMaintenance"));
const CartTagGenerator = lazyRetry(() => import("./pages/CartTagGenerator"));
const StreetLegalDealersReport = lazyRetry(() => import("./pages/StreetLegalDealersReport"));


const DeliveryPhotosPage = lazyRetry(() => import("./pages/DeliveryPhotosPage"));
const LocationPlanner = lazyRetry(() => import("./pages/LocationPlanner"));
const TechDashboard = lazyRetry(() => import("./pages/TechDashboard"));
const TechMobile = lazyRetry(() => import("./pages/TechMobile"));
const AdminDebug = lazyRetry(() => import("./pages/AdminDebug"));
const ResponsiveInventory = lazyRetry(() => import("./components/ResponsiveInventory"));
const PartQuickAction = lazyRetry(() => import("./pages/PartQuickAction"));
const InventoryCountDashboard = lazyRetry(() => import("./pages/InventoryCountDashboard"));
const DispatchOutForDelivery = lazyRetry(() => import("./pages/DispatchOutForDelivery"));
const InvoicePublicView = lazyRetry(() => import("./pages/InvoicePublicView"));  // iter 370gs
const CustomerTracking = lazyRetry(() => import("./pages/CustomerTracking"));  // public delivery tracker
const ShopTechDashboard = lazyRetry(() => import("./pages/ShopTechDashboard"));
const ShopBuildSheet = lazyRetry(() => import("./pages/ShopBuildSheet"));
const ShopAddTask = lazyRetry(() => import("./pages/ShopAddTask"));
const DetailerDashboard = lazyRetry(() => import("./pages/DetailerDashboard"));
const InventoryDashboard = lazyRetry(() => import("./pages/InventoryDashboard"));
const InventoryLayout = lazyRetry(() => import("./components/InventoryLayout"));
const StreetLegalKanban = lazyRetry(() => import("./pages/StreetLegalKanban"));
const DealerPortal = lazyRetry(() => import("./pages/DealerPortal"));  // iter 370he
// iter 370hf — global "cha-ching" toast + chime when a Street Legal
// invoice flips to paid via the Stripe webhook. Not lazy — it's a
// tiny no-render listener that must mount immediately on auth.
import { InvoicePaidNotifier } from "./components/street-legal/billing/InvoicePaidNotifier";
import UsageTracker from "./components/UsageTracker";
const BusinessDashboard = lazyRetry(() => import("./pages/BusinessDashboard"));
const PreventionPreparation = lazyRetry(() => import("./pages/PreventionPreparation"));
// iter 370bw — Jake asked us to sunset the Prevention Report and
// replace it with a much simpler tagging page. The OLD page is still
// importable (kept on /prevention-legacy below as an admin escape
// hatch) so we can revive the questionnaire flow if four tags prove
// insufficient. The NEW page renders at /prevention so the URL Jake
// had bookmarked still works.
const ServiceCallTagging = lazyRetry(() => import("./pages/ServiceCallTagging"));
const TeamReviews = lazyRetry(() => import("./pages/TeamReviews"));
const PlatformUsage = lazyRetry(() => import("./pages/PlatformUsage"));
const DailyDashboard = lazyRetry(() => import("./pages/DailyDashboard"));
// iter 370ln-C — PreparingForDelivery page sunsetted per Jake. Old
// `/preparing-for-delivery` URLs now redirect to Dispatch's Florida
// tab (`?tab=florida`). Kept the lazy import off since nothing uses
// the component any more.
const StockOverview = lazyRetry(() => import("./pages/StockOverview"));
const Rebuilds = lazyRetry(() => import("./pages/Rebuilds"));
const PartsOrders = lazyRetry(() => import("./pages/PartsOrders"));
const Warranty = lazyRetry(() => import("./pages/Warranty"));
const RoutePlanner = lazyRetry(() => import("./pages/RoutePlanner"));
const MobileHome = lazyRetry(() => import("./pages/mobile/MobileHome"));
const MobileStreetLegal = lazyRetry(() => import("./pages/mobile/MobileStreetLegal"));
const MobileShopHealth = lazyRetry(() => import("./pages/mobile/MobileShopHealth"));
const MobilePhotoUpload = lazyRetry(() => import("./pages/mobile/MobilePhotoUpload"));
const MobileWarranty = lazyRetry(() => import("./pages/mobile/MobileWarranty"));
const MobileBulletinCompose = lazyRetry(() => import("./pages/mobile/MobileBulletinCompose"));
const MobileCartLookup = lazyRetry(() => import("./pages/mobile/MobileCartLookup"));
// iter 370cd — walk-the-truck "incoming orders" flow. Three pages
// chained: manufacturer → order list → order detail (cart checklist).
const MobileIncomingOrdersIndex = lazyRetry(() => import("./pages/mobile/MobileIncomingOrdersIndex"));
const MobileIncomingOrdersList = lazyRetry(() => import("./pages/mobile/MobileIncomingOrdersList"));
const MobileIncomingOrderDetail = lazyRetry(() => import("./pages/mobile/MobileIncomingOrderDetail"));
const ChecklistPage = lazyRetry(() => import("./pages/ChecklistPage"));
const PaymentSuccess = lazyRetry(() => import("./pages/PaymentSuccess"));
import "./App.css";
import BACKEND_URL from "./lib/apiConfig";

const API = `${BACKEND_URL}/api`;

// iter 497c — PageLoader used to show a bright green spinner on a
// different dark shade (#111113) which Jake saw as "a different
// page flashing" before the real page settled. Toned it down to a
// quiet, same-background placeholder: zero visual pop on cached
// chunk loads, a subtle delayed hint on slow ones.
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-[#0A0A0A]" data-testid="page-loader">
    <p className="text-zinc-700 text-xs tracking-wide animate-pulse" style={{ animationDelay: "250ms" }}>…</p>
  </div>
);

// Retry wrapper for lazy imports — handles chunk load failures after deploys
function lazyRetry(importFn) {
  return lazy(() =>
    importFn().catch((err) => {
      // Check if this is a chunk load error (stale deploy)
      const isChunk = err && (
        err.message?.includes('Loading chunk') ||
        err.message?.includes('ChunkLoadError') ||
        err.message?.includes('Loading CSS chunk') ||
        err.message?.includes('Failed to fetch dynamically imported module') ||
        err.message?.includes("Unexpected token '<'")
      );
      if (!isChunk) throw err; // Not a chunk error — let ErrorBoundary handle

      // Retry once after delay
      return new Promise(resolve => setTimeout(resolve, 1000))
        .then(() => importFn())
        .catch(() => {
          // Cache-bust: force reload to get new index.html with correct chunk refs
          const lastReload = sessionStorage.getItem('chunk_reload');
          const now = Date.now();
          if (!lastReload || now - parseInt(lastReload, 10) > 30000) {
            sessionStorage.setItem('chunk_reload', now.toString());
            window.location.reload();
          }
          return { default: () => null };
        });
    })
  );
}

/** Shared route definitions used by both inventory and admin/dispatcher layouts.
 * Returns an array of Route elements to spread directly into Routes.
 * React Router v6 requires Routes children to be Route or Fragment elements only.
 */
function getCommonRoutes(extra = null) {
  return [
    <Route key="routes" path="routes" element={<ErrorBoundary><RouteBuilder /></ErrorBoundary>} />,
    <Route key="routes-id" path="routes/:routeId" element={<ErrorBoundary><RouteBuilder /></ErrorBoundary>} />,
    <Route key="routes-new" path="routes/new" element={<ErrorBoundary><RouteBuilder /></ErrorBoundary>} />,
    <Route key="dashboard" path="dashboard" element={<Navigate to="/daily-dashboard" replace />} />,
    // Schedule page sunset 2026-04-17 — bookmarks redirect to Dispatch
    <Route key="schedule" path="schedule" element={<Navigate to="/service-calls" replace />} />,
    <Route key="service-calls" path="service-calls" element={<ErrorBoundary><ServiceCalls /></ErrorBoundary>} />,
    <Route key="schedule-calendar" path="schedule-calendar" element={<ErrorBoundary><ScheduleCalendar /></ErrorBoundary>} />,
    <Route key="service-ticket-history" path="service-ticket/:ticketId" element={<ErrorBoundary><ServiceTicketHistory /></ErrorBoundary>} />,
    <Route key="deliveries" path="deliveries" element={<ErrorBoundary><Deliveries /></ErrorBoundary>} />,
    <Route key="knowledge" path="knowledge" element={<ErrorBoundary><KnowledgeBase /></ErrorBoundary>} />,
    <Route key="diagnostics" path="diagnostics" element={<ErrorBoundary><Diagnostics /></ErrorBoundary>} />,
    <Route key="settings" path="settings" element={<ErrorBoundary><Settings /></ErrorBoundary>} />,
    <Route key="cart-tag-generator" path="cart-tag-generator" element={<ErrorBoundary><CartTagGenerator /></ErrorBoundary>} />,

    <Route key="build-sheet-library" path="build-sheet-library" element={<ErrorBoundary><BuildSheetLibrary /></ErrorBoundary>} />,
    <Route key="inventory" path="inventory" element={<ErrorBoundary><ResponsiveInventory /></ErrorBoundary>} />,
    <Route key="inventory-part" path="inventory/part/:id" element={<ErrorBoundary><PartQuickAction /></ErrorBoundary>} />,
    <Route key="inventory-count" path="inventory-count" element={<ErrorBoundary><InventoryCountDashboard /></ErrorBoundary>} />,
    <Route key="daily-dashboard" path="daily-dashboard" element={<ErrorBoundary><DailyDashboard /></ErrorBoundary>} />,
    <Route key="preparing-for-delivery" path="preparing-for-delivery" element={<Navigate to="/service-calls?tab=florida" replace />} />,
    // Cart Inventory page sunset 2026-04-17 — bookmarks redirect to Stock Overview
    <Route key="cart-inventory" path="cart-inventory" element={<Navigate to="/stock-overview" replace />} />,
    <Route key="stock-overview" path="stock-overview" element={<ErrorBoundary><StockOverview /></ErrorBoundary>} />,
    <Route key="rebuilds" path="rebuilds" element={<ErrorBoundary><Rebuilds /></ErrorBoundary>} />,
    <Route key="parts-orders" path="parts-orders" element={<ErrorBoundary><PartsOrders /></ErrorBoundary>} />,
    <Route key="warranty" path="warranty" element={<ErrorBoundary><Warranty /></ErrorBoundary>} />,
    <Route key="plan-routes" path="plan-routes" element={<ErrorBoundary><RoutePlanner /></ErrorBoundary>} />,
    <Route key="checklist" path="checklist" element={<ErrorBoundary><ChecklistPage /></ErrorBoundary>} />,
    <Route key="recall-checklist-admin" path="recall-checklist-admin" element={<ErrorBoundary><RecallChecklistAdmin /></ErrorBoundary>} />,
    <Route key="cart-audit" path="cart-audit" element={<ErrorBoundary><CartAudit /></ErrorBoundary>} />,
    <Route key="cart-audit-log" path="cart-audit/log" element={<ErrorBoundary><CartAuditLog /></ErrorBoundary>} />,
    <Route key="manufacturer-report" path="manufacturer-report" element={<Navigate to="/warranty" replace />} />,
    ...(extra || []),
  ];
}

// Inventory role: desktop gets full dispatch layout, mobile/tablet gets simplified InventoryLayout
function InventoryResponsiveRouter({ user, onLogout }) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 1024);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  if (isMobile) {
    return (
      <Routes>
        <Route path="*" element={<InventoryLayout user={user} onLogout={onLogout} />} />
      </Routes>
    );
  }

  const floorCheckEl = canAccessFloorCheck(user)
    ? <ErrorBoundary><DailyFloorCheck /></ErrorBoundary>
    : <Navigate to="/inventory" replace />;

  return (
    <Routes>
      <Route path="/" element={<Layout user={user} onLogout={onLogout} />}>
        <Route index element={<Navigate to="/service-calls" replace />} />
        {getCommonRoutes([
          <Route key="tasks" path="tasks" element={<ErrorBoundary><Tasks /></ErrorBoundary>} />,
          <Route key="daily-floor-check" path="daily-floor-check" element={floorCheckEl} />,
        ])}
        <Route path="*" element={<Navigate to="/inventory" replace />} />
      </Route>
    </Routes>
  );
}

// Technician routing: redirect to last used dashboard or fallback
function TechLastDashboardRedirect({ user, onLogout, fallback }) {
  const userId = user?.id || '';
  const lastDashboard = localStorage.getItem(`last_dashboard_${userId}`);
  const validPaths = ["/shop", "/detailer", "/tech-mobile", "/tech", "/inventory-dash"];
  const target = lastDashboard && validPaths.includes(lastDashboard) ? lastDashboard : fallback;
  return (
    <Routes>
      <Route path="*" element={<Navigate to={target} replace />} />
    </Routes>
  );
}

// Dispatcher/Admin: mobile-sized screens redirect to /mobile, desktop shows full Layout
function DispatcherMobileRedirect({ user, onLogout }) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const location = useLocation();
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // iter 355: mobile-friendly deep-link paths that should render their
  // own mobile-first UI instead of bouncing to `/mobile`. Without this,
  // a dispatcher/admin scanning a shelf-label QR on their phone would
  // land at `/inventory/part/abc123`, get redirected to `/mobile`, and
  // lose the deep-link target even after PostLoginRedirect resolved.
  // The matched pages (PartQuickAction etc.) already render well on
  // mobile, so we pass through and let them handle their own layout.
  //
  // iter 370kt — Cart Audit pages also added. The mobile scanner
  // (/cart-audit) is explicitly designed mobile-first (Jake walks the
  // lot with his phone), and /cart-audit/log needs to be reachable too
  // so the "Log" header button on the mobile audit doesn't bounce
  // back to /mobile.
  const isMobileFriendlyDeepLink =
    /^\/inventory\/part\//.test(location.pathname) ||
    /^\/cart-audit(\/.*)?$/.test(location.pathname) ||
    /^\/parts-orders$/.test(location.pathname) || // iter 399 — Parts Orders is mobile-first for every role
    /^\/(daily-dashboard|build-sheet-library|prevention|stock-overview|location-planner|platform-usage)$/.test(location.pathname) ||
    /^\/(service-calls|routes|deliveries)(\/|$)/.test(location.pathname); // keep the shared route workspace/draft on viewport changes

  if (isMobile && !isMobileFriendlyDeepLink) {
    return <Navigate to="/mobile" replace />;
  }

  return <Layout user={user} onLogout={onLogout} />;
}

// iter 348 — one-shot deep-link redirect after login. PinLogin stashes
// the URL the user was trying to reach (e.g. `/inventory/part/:id` from
// a shelf-label QR scan) in sessionStorage. Once the authenticated
// branch mounts, this component consumes the stash and navigates there.
// Without this, a technician scanning a shelf QR while signed-out would
// get dumped on `/shop` or `/detailer` after login instead of the stock
// adjustment page the QR code was pointing at.
function PostLoginRedirect() {
  const navigate = useNavigate();
  useEffect(() => {
    let target = null;
    try {
      target = sessionStorage.getItem("pending_redirect");
      if (target) sessionStorage.removeItem("pending_redirect");
    } catch { /* sessionStorage disabled — non-fatal */ }
    if (!target) return;
    const current = window.location.pathname + window.location.search;
    if (target !== current) {
      navigate(target, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);

  // Check for existing session on mount
  useEffect(() => {
    initTheme();
    checkSession();
  }, []);

  const checkSession = async () => {
    const token = localStorage.getItem("session_token");
    const expiresAt = localStorage.getItem("session_expires");

    if (!token) {
      setIsLoading(false);
      return;
    }

    // Check if session expired locally first
    if (expiresAt && new Date() > new Date(expiresAt)) {
      handleLogout();
      setIsLoading(false);
      return;
    }

    // Trust cached session immediately — verify in background
    const cachedId = localStorage.getItem("user_id");
    const cachedName = localStorage.getItem("user_name");
    const cachedRole = localStorage.getItem("user_role");
    if (cachedId && cachedRole) {
      setIsAuthenticated(true);
      // Preload dashboard data — same as fresh login, so returning users
      // get the single-round-trip benefit too
      import("./lib/dashboardBootstrap").then(({ preloadDashboard }) => {
        preloadDashboard(
          (url, opts) => axios.get(url, { ...opts, _skipBootstrapCache: true })
        );
      }).catch(() => { /* non-fatal */ });
      import("./lib/routePrefetch").then(({ prefetchCoreRoutes }) => prefetchCoreRoutes()).catch(() => {});
      setUser({
        id: cachedId,
        name: cachedName,
        role: cachedRole,
        secondary_roles: JSON.parse(localStorage.getItem("user_secondary_roles") || "[]")
      });
      setIsLoading(false);

      // Background verify — silently logout if server rejects
      try {
        const res = await axios.post(`${API}/auth/verify?token=${token}`);
        if (res.data.valid) {
          // Update with fresh server data (role changes, etc.)
          setUser({
            id: res.data.user_id,
            name: res.data.user_name,
            role: res.data.role,
            secondary_roles: res.data.secondary_roles || []
          });
          localStorage.setItem("user_secondary_roles", JSON.stringify(res.data.secondary_roles || []));
        } else {
          handleLogout();
        }
      } catch (err) {
        if (err.response?.status === 401) {
          handleLogout();
        }
        // Network errors: stay logged in with cached data
      }
      return;
    }

    // No cached user data — must verify with server before proceeding
    try {
      const res = await axios.post(`${API}/auth/verify?token=${token}`);
      if (res.data.valid) {
        setIsAuthenticated(true);
        setUser({
          id: res.data.user_id,
          name: res.data.user_name,
          role: res.data.role,
          secondary_roles: res.data.secondary_roles || []
        });
        localStorage.setItem("user_id", res.data.user_id);
        localStorage.setItem("user_name", res.data.user_name);
        localStorage.setItem("user_role", res.data.role);
        localStorage.setItem("user_secondary_roles", JSON.stringify(res.data.secondary_roles || []));
      } else {
        handleLogout();
      }
    } catch (err) {
      if (err.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = (userData) => {
    setIsAuthenticated(true);
    setUser({
      id: userData.user_id,
      name: userData.user_name,
      role: userData.role,
      secondary_roles: userData.secondary_roles || []
    });
    
    // Store user info for Tech Dashboard
    localStorage.setItem("user_id", userData.user_id);
    localStorage.setItem("user_name", userData.user_name);
    localStorage.setItem("user_role", userData.role);
    localStorage.setItem("user_secondary_roles", JSON.stringify(userData.secondary_roles || []));

    // Preload dashboard data in one round-trip so the 20 components that
    // hydrate on first render hit a local cache instead of firing 20
    // separate GETs. Non-blocking, non-fatal if it fails.
    import("./lib/dashboardBootstrap").then(({ preloadDashboard }) => {
      // Pass axios so auth cookies / interceptors are preserved.
      // _skipBootstrapCache avoids infinite loop (this IS the bootstrap).
      preloadDashboard(
        (url, opts) => axios.get(url, { ...opts, _skipBootstrapCache: true })
      );
    }).catch(() => { /* non-fatal */ });
    import("./lib/routePrefetch").then(({ prefetchCoreRoutes }) => prefetchCoreRoutes()).catch(() => {});
  };

  const handleLogout = () => {
    const token = localStorage.getItem("session_token");
    if (token) {
      // Invalidate session on backend (fire and forget)
      axios.post(`${API}/auth/logout?token=${token}`).catch(() => {});
    }
    
    localStorage.removeItem("session_token");
    localStorage.removeItem("user_id");
    localStorage.removeItem("user_name");
    localStorage.removeItem("user_role");
    localStorage.removeItem("user_secondary_roles");
    localStorage.removeItem("session_expires");
    
    setIsAuthenticated(false);
    setUser(null);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#111113] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#39FF14] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Show PIN login if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="App min-h-screen bg-[#111113]">
        <ReconnectingBanner />
        <Toaster position="bottom-right" richColors />
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Driver Portal - always accessible without PIN */}
              <Route path="/driver/:shareToken" element={<DriverPortal />} />
              {/* Payment Success - public page for customers */}
              <Route path="/payment-success" element={<PaymentSuccess />} />
              {/* QR-scan out-for-delivery — tokenized, no login required */}
              <Route path="/d/:token" element={<DispatchOutForDelivery />} />
              {/* Customer delivery tracker — tokenized, no login required */}
              <Route path="/track/:token" element={<CustomerTracking />} />
              <Route path="/t/:token" element={<CustomerTracking />} />
              {/* iter 370gs — Service invoice public viewer (customer-facing) */}
              <Route path="/i/:invoiceId" element={<InvoicePublicView />} />
              {/* iter 370he — Dealer portal: phone+PIN auth, parallel to staff session.
                  Must be declared here too (in the unauthenticated branch) — otherwise
                  the catch-all below hijacks /dealer-portal and renders the staff
                  PinLogin page for external dealers who have no staff session. */}
              <Route path="/dealer-portal" element={<ErrorBoundary><DealerPortal /></ErrorBoundary>} />
              <Route path="/dealer-portal/*" element={<ErrorBoundary><DealerPortal /></ErrorBoundary>} />
              {/* All other routes redirect to login */}
              <Route path="*" element={<PinLogin onLogin={handleLogin} />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </div>
    );
  }

  // Check if user is a technician - they should only see Tech Dashboard
  const isTechnician = user?.role?.toLowerCase() === 'technician';
  
  // Check if user is a driver - they should see the mobile tech/driver dashboard
  const isDriver = user?.role?.toLowerCase() === 'driver';
  
  // Check if user is inventory role - they should see inventory count dashboard
  const isInventory = user?.role?.toLowerCase() === 'inventory';
  
  // Check if user has multiple roles (can toggle between dashboards)
  const secondaryRoles = user?.secondary_roles || [];
  const hasMultipleRoles = secondaryRoles.length > 0;
  const canAccessShop = secondaryRoles.includes('shop');
  const canAccessServiceTech = isTechnician || secondaryRoles.includes('service');
  const canAccessDetailer = secondaryRoles.includes('detailer');
  const canAccessInventory = isInventory || secondaryRoles.includes('inventory');
  
  // Determine default dashboard for technicians
  const techHasService = secondaryRoles.includes('service');
  const techDefaultsToDetailer = isTechnician && canAccessDetailer && !techHasService;
  const techDefaultsToShop = isTechnician && canAccessShop && !canAccessDetailer && !techHasService;
  // iter 386h — Brady: converted to a technician account (381o, for shop
  // time tracking) which silently locked him out of the desktop dispatch
  // app (Route Builder / optimize). Technicians with the 'dispatch'
  // secondary role get the full dispatcher route tree; the tech
  // dashboards stay reachable (registered unconditionally above).
  const techHasDispatch = isTechnician && secondaryRoles.includes('dispatch');
  
  // Dispatchers with inventory secondary role default to inventory dashboard
  const dispatcherDefaultsToInventory = !isTechnician && !isInventory && secondaryRoles.includes('inventory');

  return (
    <div className="App min-h-screen bg-[#111113]">
      <ReconnectingBanner />
      <Toaster position="bottom-right" richColors />
      <ChunkErrorBoundary>
        <BrowserRouter>
        <PostLoginRedirect />
        <VersionChecker />
        <UpdateAvailableBanner />
        {/* iter 370hf — listens for street_legal.invoice_paid WS events
            and surfaces a toast + soft chime. Mounts inside the
            authenticated branch only so dealer-portal / driver-portal
            visitors don't get staff-side notifications. */}
        {/* iter 373 — single shared WS connection for ALL authenticated routes */}
        <GlobalWebSocket />
        <UsageTracker />
        <InvoicePaidNotifier />
        <ErrorBoundary fallbackMessage="The application encountered an error. Please try again.">
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Driver Portal - standalone without sidebar */}
              <Route path="/driver/:shareToken" element={<DriverPortal />} />
              
              {/* Payment Success - public page for customers after paying */}
              <Route path="/payment-success" element={<PaymentSuccess />} />

              {/* QR-scan out-for-delivery — tokenized, no login needed */}
              <Route path="/d/:token" element={<DispatchOutForDelivery />} />
              {/* Customer delivery tracker — tokenized, no login needed */}
              <Route path="/track/:token" element={<CustomerTracking />} />
              <Route path="/t/:token" element={<CustomerTracking />} />
              {/* iter 370gs — Service invoice public viewer */}
              <Route path="/i/:invoiceId" element={<InvoicePublicView />} />

              {/* iter 370he — Dealer portal: phone+PIN auth, no staff session required */}
              <Route path="/dealer-portal" element={<ErrorBoundary><DealerPortal /></ErrorBoundary>} />
              <Route path="/dealer-portal/*" element={<ErrorBoundary><DealerPortal /></ErrorBoundary>} />
              
              {/* Mobile Routes - accessible by all authenticated users */}
              <Route path="/mobile" element={<MobileHome user={user} onLogout={handleLogout} />} />
              <Route path="/mobile/street-legal" element={<MobileStreetLegal />} />
              <Route path="/mobile/shop-health" element={<MobileShopHealth />} />
              <Route path="/mobile/street-legal/photos/:id" element={<MobilePhotoUpload />} />
              <Route path="/mobile/warranty" element={<MobileWarranty />} />
              <Route path="/mobile/bulletin/new" element={<MobileBulletinCompose />} />
              <Route path="/mobile/cart-lookup" element={<MobileCartLookup />} />
              {/* iter 370kt — Cart Inventory Audit: standalone fullscreen
                  pages (no desktop sidebar wrapper). The mobile scanner
                  at /cart-audit + the desktop log at /cart-audit/log
                  each have their own back/header/FAB chrome, and Jake
                  walks the lot with his phone — sidebar would just get
                  in the way. Declared here as top-level routes so they
                  take precedence over the in-Layout nested routes. */}
              <Route path="/cart-audit" element={<ErrorBoundary><CartAudit /></ErrorBoundary>} />
              <Route path="/cart-audit/log" element={<ErrorBoundary><CartAuditLog /></ErrorBoundary>} />
              {/* iter 370cd — Incoming Orders (walk-the-truck) flow */}
              <Route path="/mobile/incoming-orders" element={<ErrorBoundary><MobileIncomingOrdersIndex /></ErrorBoundary>} />
              <Route path="/mobile/incoming-orders/:manufacturer" element={<ErrorBoundary><MobileIncomingOrdersList /></ErrorBoundary>} />
              <Route path="/mobile/incoming-orders/order/:orderId" element={<ErrorBoundary><MobileIncomingOrderDetail /></ErrorBoundary>} />
              
              {/* Shop Tech Routes - always accessible */}
              <Route path="/shop" element={<ErrorBoundary><ShopTechDashboard /></ErrorBoundary>} />
              <Route path="/shop/build/:cartId" element={<ErrorBoundary><ShopBuildSheet /></ErrorBoundary>} />
              <Route path="/shop/add-task" element={<ErrorBoundary><ShopAddTask /></ErrorBoundary>} />
              
              {/* Detailer Routes - always accessible */}
              <Route path="/detailer" element={<ErrorBoundary><DetailerDashboard /></ErrorBoundary>} />

              {/* iter 399 — Parts Orders for roles without the sidebar shell (techs, drivers, detailers) */}
              {(isDriver || (isTechnician && !techHasDispatch)) && (
                <Route path="/parts-orders" element={<ErrorBoundary><PartsOrders standalone /></ErrorBoundary>} />
              )}
              
              {/* Inventory Dashboard - standalone for inventory role */}
              <Route path="/inventory-dash" element={<ErrorBoundary><InventoryDashboard /></ErrorBoundary>} />
              
              {/* Tech Mobile - Ultra-simple mobile view for technicians */}
              <Route path="/tech-mobile" element={<ErrorBoundary><TechMobile user={user} onLogout={handleLogout} /></ErrorBoundary>} />
              
              {/* Tech Dashboard - Full features dashboard */}
              <Route path="/tech" element={<ErrorBoundary><TechDashboard user={user} onLogout={handleLogout} /></ErrorBoundary>} />
              
              {/* Technician routing — remembers last active dashboard */}
              {isDriver ? (
                <Route path="*" element={<Navigate to="/tech-mobile" replace />} />
              ) : isTechnician && !techHasDispatch ? (
                <Route path="*" element={<TechLastDashboardRedirect user={user} onLogout={handleLogout}
                  fallback={techDefaultsToShop ? "/shop" : techDefaultsToDetailer ? "/detailer" : "/tech-mobile"} />} />
              ) : isInventory ? (
                /* Inventory role: full dispatch on desktop, simplified layout on mobile/tablet */
                <Route path="*" element={<InventoryResponsiveRouter user={user} onLogout={handleLogout} />} />
              ) : dispatcherDefaultsToInventory ? (
                /* Dispatcher with inventory secondary — default to inventory dashboard */
                <Route path="*" element={<Navigate to="/inventory-dash" replace />} />
              ) : (
                <>
                  {/* Main app with sidebar - for dispatchers/admins */}
                  <Route path="/" element={<DispatcherMobileRedirect user={user} onLogout={handleLogout} />}>
                    <Route index element={<Navigate to="/service-calls" replace />} />
                    {getCommonRoutes([
                      <Route key="tasks" path="tasks" element={<Navigate to="/bulletins" replace />} />,
                      <Route key="daily-floor-check" path="daily-floor-check" element={canAccessFloorCheck(user) ? <ErrorBoundary><DailyFloorCheck /></ErrorBoundary> : <Navigate to="/daily-dashboard" replace />} />,
                      <Route key="bulletins" path="bulletins" element={<ErrorBoundary><Bulletins /></ErrorBoundary>} />,
                      <Route key="customers" path="customers/:customerId" element={<ErrorBoundary><CustomerHistory /></ErrorBoundary>} />,
                      <Route key="fleet" path="fleet-maintenance" element={<ErrorBoundary><FleetMaintenance /></ErrorBoundary>} />,
                      <Route key="delivery-photos" path="delivery-photos" element={<ErrorBoundary><DeliveryPhotosPage /></ErrorBoundary>} />,
                      <Route key="location" path="location-planner" element={<ErrorBoundary><LocationPlanner /></ErrorBoundary>} />,
                      <Route key="tech-dash" path="tech-dashboard" element={<ErrorBoundary><TechDashboard /></ErrorBoundary>} />,
                      <Route key="shop-perf" path="shop-performance" element={<Navigate to="/dashboard" replace />} />,
                      <Route key="queue" path="queue" element={<Navigate to="/daily-dashboard" replace />} />,
                      <Route key="street-legal" path="street-legal" element={<Navigate to="/street-legal/kanban" replace />} />,
                      // Street Legal list view sunset 2026-04-17 — bookmarks redirect to Kanban
                      <Route key="street-legal-list" path="street-legal/list" element={<Navigate to="/street-legal/kanban" replace />} />,
                      <Route key="street-legal-kanban" path="street-legal/kanban" element={<ErrorBoundary><StreetLegalKanban /></ErrorBoundary>} />,
                      <Route key="street-legal-dealers-report" path="street-legal/dealers-report" element={<ErrorBoundary><StreetLegalDealersReport /></ErrorBoundary>} />,
                      <Route key="business-dashboard" path="business" element={<Navigate to="/daily-dashboard" replace />} />,
                      <Route key="operations" path="operations" element={<Navigate to="/daily-dashboard" replace />} />,
                      <Route key="debug" path="admin/debug" element={<AdminDebug />} />,
                      /* iter 350: /admin/error-logs + /sync-monitor routes
                         removed. Error logs live inline on /settings;
                         sync failures surface via a top-of-app banner. */
                      /* iter 370bw — /prevention now hosts the new
                         simplified Service Call Tagging page. The old
                         Prevention Report remains available at
                         /prevention-legacy for one-off lookups (also
                         hidden from sidebar nav). */
                      <Route key="prevention" path="prevention" element={<ErrorBoundary><ServiceCallTagging /></ErrorBoundary>} />,
                      <Route key="prevention-legacy" path="prevention-legacy" element={<ErrorBoundary><PreventionPreparation /></ErrorBoundary>} />,
                      <Route key="team-reviews" path="team-reviews" element={canAccessTeamReviews(user) ? <ErrorBoundary><TeamReviews /></ErrorBoundary> : <Navigate to="/daily-dashboard" replace />} />,
                      <Route key="platform-usage" path="platform-usage" element={user?.role?.toLowerCase() === 'admin' ? <ErrorBoundary><PlatformUsage /></ErrorBoundary> : <Navigate to="/daily-dashboard" replace />} />,
                    ])}
                  </Route>
                  {/* Catch-all: redirect unknown paths (e.g. /login after auth) to dashboard */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </>
              )}
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </BrowserRouter>
      </ChunkErrorBoundary>
    </div>
  );
}

function VersionChecker() {
  useVersionCheck();
  return null;
}

export default App;
